import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAllSuperlatives,
  selectCurrentSuperlativeIndex,
  getSuperlativesAction,
} from "../superlativeSlice";
import { selectGameId } from "../../gamestate/gameStateSlice";
import PhoneListItem from "./PhoneListItem";

const Root = styled(Box)({
  padding: "16px 12px 24px",
  textAlign: "center",
  minHeight: "100%",
});

const Title = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "0.85rem",
  letterSpacing: "0.25em",
  color: "#1a7a72",
  textTransform: "uppercase",
  fontWeight: 800,
  margin: "8px 0 14px",
});

const Featured = styled(Box)({
  background: "#fff",
  borderRadius: 16,
  padding: "18px 16px",
  border: "2px solid rgba(46,196,182,0.35)",
  boxShadow: "0 10px 24px rgba(46,196,182,0.25)",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  marginBottom: 12,
});

const Accent = styled(Box)({
  position: "absolute",
  top: 0, left: 0, right: 0,
  height: 5,
  background: "linear-gradient(90deg, #ffe066 0%, #2ec4b6 100%)",
});

const FLabel = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "0.85rem",
  letterSpacing: "0.22em",
  color: "#1a7a72",
  textTransform: "uppercase",
  fontWeight: 900,
  marginTop: 6,
});

const FSub = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "1.25rem",
  color: "#1c1c1c",
  fontWeight: 700,
  lineHeight: 1.15,
  margin: "6px 0 10px",
});

const FWinner = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "2.4rem",
  color: "#222",
  lineHeight: 1,
  margin: "6px 0",
});

const FDetail = styled(Typography)({
  fontSize: "0.78rem",
  color: "#666",
});

const SectionLabel = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "0.65rem",
  letterSpacing: "0.22em",
  color: "#888",
  textTransform: "uppercase",
  textAlign: "left",
  margin: "16px 4px 8px",
});

const List = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

/**
 * Earlier list grows by one each time currentIndex advances. The just-added
 * item starts in `expanding` state and transitions to full size on the next
 * paint, producing the smooth slide-down for items below.
 */
const PhoneReveal = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const superlatives = useSelector(selectAllSuperlatives);
  const currentIndex = useSelector(selectCurrentSuperlativeIndex);

  const [expandingIdx, setExpandingIdx] = useState(null);
  const lastIndexRef = useRef(currentIndex);

  useEffect(() => {
    if (gameId) dispatch(getSuperlativesAction(gameId));
  }, [gameId, dispatch]);

  // When index advances, the previously-featured superlative lands in the list
  // at slot 0 and should animate in.
  useEffect(() => {
    if (currentIndex > lastIndexRef.current) {
      setExpandingIdx(currentIndex - 1);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setExpandingIdx(null));
      });
      lastIndexRef.current = currentIndex;
      return () => cancelAnimationFrame(id);
    }
    lastIndexRef.current = currentIndex;
  }, [currentIndex]);

  if (superlatives.length === 0) return null;
  const current = superlatives[currentIndex];
  // Prior superlatives, newest first
  const earlier = superlatives.slice(0, currentIndex).reverse();

  return (
    <Root>
      <Title>The Highlights</Title>
      {current && (
        <Featured>
          <Accent />
          <FLabel>{current.header}</FLabel>
          <FSub>{current.subhead}</FSub>
          <FWinner>{current.winner}</FWinner>
          {current.winnerDetail && <FDetail>{current.winnerDetail}</FDetail>}
        </Featured>
      )}
      {earlier.length > 0 && <SectionLabel>Earlier</SectionLabel>}
      <List>
        {earlier.map((s, i) => {
          // Only the freshly-added item (index = currentIndex - 1, which is earlier[0]) animates
          const isFresh = i === 0 && expandingIdx === currentIndex - 1;
          return <PhoneListItem key={s.id} superlative={s} expanding={isFresh} />;
        })}
      </List>
    </Root>
  );
};

export default PhoneReveal;
